import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import {
  convertEpochToDateString,
  formatDateTimeToDate
} from '../../../../../utils/commonFunctions';

type ReturnedType = any;
type ThunkArg = {
  currentOdometer: number;
  dueCriteria: string;
  dueOdometerDistance: number;
  intervalType: string;
  maintenanceDate: number;
  make: string;
  nextDueDate: number;
  part: string;
  serviceInterval: number;
  type: string;
  vehicleNumber: string;
  vehicleType: string;
};

interface Maintenance {
  currentOdometer: number;
  dueCriteria: string;
  dueOdometerDistance: number;
  intervalType: string;
  maintenanceDate: number;
  make: string;
  nextDueDate: number;
  part: string;
  serviceInterval: number;
  type: string;
  vehicleNumber: string;
  vehicleType: string;
}

type initialStateProps = {
  isLoading: boolean;
  type: string;
  data: any;
  error: string | null;
  count: null;
};

const initialState: initialStateProps = {
  isLoading: false,
  type: 'addMaintenance',
  data: [],
  error: null,
  count: null
};

type vehicleNumberInitialStateProps = {
  isLoading: boolean;
  vehicleNumberData: any;
  error: null;
};

const vehicleNumberInitialState: vehicleNumberInitialStateProps = {
  isLoading: false,
  vehicleNumberData: [],
  error: null
};

type partInitialStateProps = {
  isLoading: boolean;
  partData: any;
  error: null;
};

const partInitialState: partInitialStateProps = {
  isLoading: false,
  partData: [],
  error: null
};

type makeInitialStateProps = {
  isLoading: boolean;
  makeData: any;
  error: null;
};

const makeInitialState: makeInitialStateProps = {
  isLoading: false,
  makeData: [],
  error: null
};

type typeInitialStateProps = {
  isLoading: boolean;
  typeData: any;
  error: null;
};

const typeInitialState: typeInitialStateProps = {
  isLoading: false,
  typeData: [],
  error: null
};

// add maintenance
export const addMaintenance = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/addMaintenance',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_MAINTENANCE}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// add maintenance slice
const addMaintenanceSlice = createSlice({
  name: 'addMaintenance',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addMaintenance.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addMaintenance.fulfilled, (state, action: any) => {
      state.data = null;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addMaintenance.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// get maintenance
export const getMaintenance = createAsyncThunk<any, any>(
  'api/getMaintenance',
  async (data, thunkApi) => {
    const { signal } = data;
    try {
      let URL = `${URLs.GET_ALL_MAINTENANCE}?deviceid=${data.deviceID}&enddate=${data.endDate}&startdate=${data.startDate}&pageno=${data.pageno}&pagesize=${data.pagesize}&sortBy=${data.sortBy}`;

      const response: any = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      if (
        response?.data?.data?.maintenanceList &&
        response?.data?.data?.maintenanceList?.length > 0
      ) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'success',
            message: 'Maintenance report generated successfully'
          })
        );
      } else {
        thunkApi.dispatch(
          updateToast({
            show: true,
            severity: 'warning',
            message: 'No reports found'
          })
        );
      }
      return response?.data.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error.response.data.message
              : error.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get maintenance slice
const getMaintenanceSlice = createSlice({
  name: 'getMaintenance',
  initialState: initialState,
  reducers: {
    clearData: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMaintenance.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.type = 'getMaintenance';
      })
      .addCase(getMaintenance.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        const { maintenanceList, count } = action.payload;
        state.data = maintenanceList?.map((item: any) => ({
          id: item?.id,
          maintenanceDate: item?.maintenanceDate,
          maintenanceDateFormat: formatDateTimeToDate(
            convertEpochToDateString(item?.maintenanceDate)
          ),
          vehicleType: item?.vehicleType,
          vehicleNumber: item?.vehicleNumber,
          part: item?.part,
          make: item?.make,
          type: item?.type,
          dueCriteria: item?.dueCriteria,
          serviceInterval: item?.serviceInterval || 'NA',
          intervalType: item?.intervalType || 'NA',
          nextDueDate: item?.nextDueDate || 'NA',
          nextDueDateFormat:
            formatDateTimeToDate(convertEpochToDateString(item?.nextDueDate)) || 'NA',
          currentOdometer: item?.currentOdometer || 'NA',
          dueOdometerDistance: item?.dueOdometerDistance || 'NA'
        }));
        state.count = count;
        state.error = null;
      })
      .addCase(getMaintenance.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get truck number
export const truckNumbers = createAsyncThunk<any, any>(
  'truck/numbers',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.VEHICLE_NUMBERS}`,
        signal: data
      });
      return response?.data?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

// get truck number slice
export const truckNumbersSlice = createSlice({
  name: 'truckNumbers',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(truckNumbers.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(truckNumbers.fulfilled, (state, action: PayloadAction<Maintenance[]>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(truckNumbers.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get vehicle type
export const getVehicleType = createAsyncThunk('vehicle/type', async (data, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${URLs.VEHICLE_TYPE}`
    });
    return response?.data?.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// get vehicle type slice
export const vehicleTypeSlice = createSlice({
  name: 'getVehicleType',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getVehicleType.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(
        getVehicleType.fulfilled,
        (state, action: PayloadAction<Maintenance[]>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(getVehicleType.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get vehicle number
export const getVehicleNumber = createAsyncThunk<any, any>(
  'vehicle/number',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.VEHICLE_TYPE}?vehicleType=${data}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get vehicle number slice
export const vehicleNumberSlice = createSlice({
  name: 'getVehicleNumber',
  initialState: vehicleNumberInitialState,
  reducers: {
    clearVehicleNumberData: state => {
      state.vehicleNumberData = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getVehicleNumber.pending, state => {
        state.isLoading = true;
        state.vehicleNumberData = [];
      })
      .addCase(
        getVehicleNumber.fulfilled,
        (state, action: PayloadAction<Maintenance[]>) => {
          state.isLoading = false;
          state.vehicleNumberData = action.payload;
          state.error = null;
        }
      )
      .addCase(getVehicleNumber.rejected, (state, action: any) => {
        state.isLoading = false;
        state.vehicleNumberData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get part dropdown
export const partDropdown = createAsyncThunk('part/dropdown', async (data, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${URLs.PART_DROPDOWN}`
    });
    return response?.data?.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// get part dropdown slice
export const partDropdownSlice = createSlice({
  name: 'partDropdown',
  initialState: partInitialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(partDropdown.pending, state => {
        state.isLoading = true;
        state.partData = [];
      })
      .addCase(partDropdown.fulfilled, (state, action: PayloadAction<Maintenance[]>) => {
        state.isLoading = false;
        state.partData = action.payload;
        state.error = null;
      })
      .addCase(partDropdown.rejected, (state, action: any) => {
        state.isLoading = false;
        state.partData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get make dropdown
export const makeDropdown = createAsyncThunk<any, any>(
  'make/dropdown',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.PART_DROPDOWN}?part=${data}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get make dropdown slice
export const makeDropdownSlice = createSlice({
  name: 'makeDropdown',
  initialState: makeInitialState,
  reducers: {
    clearMakeData: state => {
      state.makeData = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(makeDropdown.pending, state => {
        state.isLoading = true;
        state.makeData = [];
      })
      .addCase(makeDropdown.fulfilled, (state, action: PayloadAction<Maintenance[]>) => {
        state.isLoading = false;
        state.makeData = action.payload;
        state.error = null;
      })
      .addCase(makeDropdown.rejected, (state, action: any) => {
        state.isLoading = false;
        state.makeData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get type dropdown
export const typeDropdown = createAsyncThunk<any, any>(
  'type/dropdown',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.PART_DROPDOWN}?part=${data.part}&make=${data.make}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get type dropdown slice
export const typeDropdownSlice = createSlice({
  name: 'typeDropdown',
  initialState: typeInitialState,
  reducers: {
    clearTypeData: state => {
      state.typeData = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(typeDropdown.pending, state => {
        state.isLoading = true;
        state.typeData = [];
      })
      .addCase(typeDropdown.fulfilled, (state, action: PayloadAction<Maintenance[]>) => {
        state.isLoading = false;
        state.typeData = action.payload;
        state.error = null;
      })
      .addCase(typeDropdown.rejected, (state, action: any) => {
        state.isLoading = false;
        state.typeData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

export const addMaintenanceReducer = addMaintenanceSlice.reducer;
export const getMaintenanceReducer = getMaintenanceSlice.reducer;
export const truckNumbersReducer = truckNumbersSlice.reducer;
export const vehicleTypeReducer = vehicleTypeSlice.reducer;
export const vehicleNumberReducer = vehicleNumberSlice.reducer;
export const partDropDownReducer = partDropdownSlice.reducer;
export const makeDropDownReducer = makeDropdownSlice.reducer;
export const typeDropDownReducer = typeDropdownSlice.reducer;
export const { clearMakeData } = makeDropdownSlice.actions;
export const { clearTypeData } = typeDropdownSlice.actions;
export const { clearVehicleNumberData } = vehicleNumberSlice.actions;
export const { clearData } = getMaintenanceSlice.actions;
